import React, { Fragment } from 'react'
import { debounce } from 'lodash'

import { monetize } from '../../lib/formats'
import { ColorCurrencyFormatter } from '../UI/FormatProviders'
import DisplayField from '../UI/DisplayField'
import ValidatedTextInput from '../UI/ValidatedTextInput'
import BooleanSelect from '../UI/BooleanSelect'
import SmallDeleteButton from '../UI/SmallDeleteButton'
import { ServiceHeader } from './QuoteFormComponents'
import './styles/line-item-input.css'

// Internal components ---------------------------------------------------------

const OriginalLineItemSummary = ({ lineItem }) => {
  const {
    description,
    number,
    formattedRate,
    formattedCost,
    adjustment,
    formattedTotal,
    formattedTotalCost,
    totalMargin
  } = lineItem.attributes

  const adjustmenSignal = adjustment > 0 ? '+' : '-'

  return (
    <main className="original-line-item-summary">
      <div className="description truncate-text">
        <strong>Original line item:</strong> {description}
      </div>
      <div className="summary">
        <strong>Rate:</strong> {number} &times; {formattedRate}{' '}
        {adjustment !== 0 && (
          <Fragment>
            {' '}
            {adjustmenSignal} {monetize(Math.abs(adjustment))}{' '}
          </Fragment>
        )}
        = <em>{formattedTotal}</em> {' | '}
        <strong>Cost:</strong> {number} &times; {formattedCost} ={' '}
        <em>{formattedTotalCost}</em> {' | '}
        <strong>Margin:</strong> <ColorCurrencyFormatter value={totalMargin} />
      </div>
    </main>
  )
}

/*
MAIN COMPONENT -----------------------------------------------------------------

Props:
- actualLineItem object
- originalLineItem object (optional - if exists)
- service object
- actualId: id of the corresponding actual
- validatedOnChange: callback function for onChange, but including a
  validation flag and field name:
  callback(actualLineItem, Bool(isValid), this.props.name || 'actualLineItem')
- deleteCallback: callback function for the delete button. Button is only shown
  if this is present.
  callback(event, actualLineItemId)
*/
class ActualLineItemInput extends React.PureComponent {
  constructor(props) {
    super(props)
    const { actualLineItem, originalLineItem, service } = this.props
    const attributes = actualLineItem.attributes

    // We'll refer back to these guys later, but they're not necessarily state
    this.serviceAttrs = service.attributes
    this.originalAttributes = originalLineItem
      ? originalLineItem.attributes
      : {}

    // Rate/actualRate are annyoing attributes.
    // If it's null or undefined we use the service rate
    // else we should use whatever value is inside - including zero
    const actualRate =
      attributes.actualRate === null || attributes.actualRate === undefined
        ? this.serviceAttrs.rate
        : attributes.actualRate

    this.state = {
      error: '',
      actualLineItemId: actualLineItem.id,
      actualTotal: attributes.actualTotal || 0.0,
      event: attributes.event,
      description: attributes.description || '',
      actualNumber: (attributes.actualNumber) === null ? this.originalAttributes.number : attributes.actualNumber,
      actualRate: actualRate,
      actualRateIncludingAdjustment: attributes.actualRateIncludingAdjustment,
      rateUnit: attributes.rateUnit || this.serviceAttrs.rateUnit,
      formattedActualRate: attributes.formattedActualRate,
      formattedActualRateIncludingAdjustment: attributes.formattedActualRateIncludingAdjustment,
      actualAdjustment:
        attributes.actualAdjustment ||
        this.originalAttributes.adjustment ||
        '0.00',
      serviceId: attributes.serviceId || this.serviceAttrs.id,
      service: service,
      serviceRate: this.serviceAttrs.rate,
      serviceCost: this.serviceAttrs.cost,
      actualCost:
        attributes.actualCost ||
        this.originalAttributes.cost ||
        this.serviceAttrs.cost,
      formattedActualCost:
        attributes.formattedActualCost ||
        this.originalAttributes.formattedCost ||
        this.serviceAttrs.formattedCost,
      hideClientRates: Boolean(attributes.hideClientRates),
      cloned: Boolean(attributes.cloned)
    }

    // Debouncing this._validatedOnChange() for performance
    this._validatedOnChange = debounce(this._validatedOnChange, 50)
  }

  componentWillMount() {
    this._updateFields('actualNumber')
  }

  _change = (value, valid, attribute) => {
    let updates = {}
    updates[attribute] = value
    this.setState(updates, () => this._updateFields(attribute))
  }

  _delete = ev => {
    const { deleteCallback } = this.props
    const { actualLineItemId } = this.state
    if (!deleteCallback) return
    deleteCallback(ev, actualLineItemId)
  }

  _getRate = () =>
    this.originalAttributes.rate || Number(this.serviceAttrs.rate)

  _getActualRate = () =>
    String(this.state.actualRate) === ''
      ? this._getRate()
      : Number(this.state.actualRate)

  _updateFields = updatedAttribute => {
    const { actualNumber, actualAdjustment } = this.state
    if (updatedAttribute.match(/number|rate|adjustment/i)) {
      const actualTotal =
        Number(actualNumber) * this._getActualRate() + Number(actualAdjustment)
      this.setState({ actualTotal }, this._validatedOnChange)
    } else {
      this._validatedOnChange()
    }
  }

  _validatedOnChange = () => {
    const { validatedOnChange, actualId, name } = this.props
    const {
      serviceId,
      description,
      actualNumber,
      actualCost,
      actualAdjustment,
      actualTotal,
      cloned
    } = this.state

    if (!validatedOnChange) return

    const attributes = {
      actualId,
      serviceId,
      description,
      number: this.originalAttributes.number,
      rate: this._getRate(),
      cost: this.originalAttributes.cost,
      adjustment: this.originalAttributes.adjustment,
      total: this.originalAttributes.total,
      actualNumber,
      actualRate: this._getActualRate(),
      actualCost,
      actualAdjustment,
      actualTotal,
      cloned
    }
    validatedOnChange(attributes, true, name || 'actualLineItem')
  }

  render() {
    const { deleteCallback, originalLineItem } = this.props
    const {
      service,
      description,
      actualNumber,
      actualCost,
      actualRate,
      serviceRate,
      serviceCost,
      actualAdjustment,
      actualTotal,
      hideClientRates,
      cloned
    } = this.state

    return (
      <main className="line-item-input">
        <ServiceHeader service={service} />

        {originalLineItem &&
          cloned && <OriginalLineItemSummary lineItem={originalLineItem} />}

        <section className="input-area">
          <section className="main-container">
            <div className="row">
              <div className="small">
                <BooleanSelect
                  name="hideClientRates"
                  floatingLabelText="Show only total to client?"
                  value={hideClientRates}
                  onChange={(ev, index, value) =>
                    this._change(value, true, 'hideClientRates')
                  }
                  fullWidth
                />
              </div>

              <div className="tiny">
                <ValidatedTextInput
                  name="actualNumber"
                  value={(actualNumber === 0) ? '0' : (actualNumber || 1)}
                  floatingLabelText="Actual Qty"
                  pattern={/^(\d+)\.?(\d+)?$/}
                  errorMessage="Invalid"
                  validatedOnChange={this._change}
                  alignRight
                  fullWidth
                  immediateValidation
                />
              </div>

              <div className="tiny">
                <ValidatedTextInput
                  name="actualCost"
                  value={actualCost === 0 ? '0.00' : actualCost}
                  floatingLabelText="Actual cost"
                  hintText={serviceCost}
                  pattern={/^(\d+)\.?(\d+)?$/}
                  errorMessage="Invalid"
                  validatedOnChange={this._change}
                  alignRight
                  fullWidth
                  immediateValidation
                />
              </div>

              <div className="tiny">
                <ValidatedTextInput
                  name="actualRate"
                  value={actualRate === 0 ? '0.00' : actualRate}
                  floatingLabelText="Actual rate"
                  hintText={serviceRate}
                  pattern={/^(\d+)\.?(\d+)?$/}
                  errorMessage="Invalid"
                  validatedOnChange={this._change}
                  alignRight
                  fullWidth
                  immediateValidation
                />
              </div>

              <div className="tiny">
                <ValidatedTextInput
                  name="actualAdjustment"
                  value={actualAdjustment}
                  floatingLabelText="Actual adjustment"
                  pattern={/^-?(\d+)\.?(\d+)?$/}
                  errorMessage="Invalid"
                  validatedOnChange={this._change}
                  alignRight
                  fullWidth
                  immediateValidation
                />
              </div>
            </div>

            <div className="row">
              <div className="full-width">
                <ValidatedTextInput
                  name="description"
                  value={description || service.attributes.name}
                  floatingLabelText="Client-facing service description (leave blank to use service name)"
                  validatedOnChange={this._change}
                  multiLine
                  rows={2}
                  rowsMax={15}
                  fullWidth
                />
              </div>
            </div>
          </section>

          <section className="total-container">
            <div className="total">
              <DisplayField
                name="actualTotal"
                value={monetize(actualTotal)}
                floatingLabelText="Line total"
                alignRight
                fullWidth
              />
            </div>

            {deleteCallback && (
              <div className="delete">
                <SmallDeleteButton
                  onTouchTap={deleteCallback}
                  style={{ marginTop: '26px' }}
                />
              </div>
            )}
          </section>
        </section>
      </main>
    )
  }
}

export default ActualLineItemInput



// WEBPACK FOOTER //
// ./src/components/Quotes/ActualLineItemInput.jsx